import React, { useEffect, useRef, useState, useMemo } from "react";
import styled from "styled-components";
import ExtensionInputGroup, { StyledLabel } from "./ExtensionInputGroup";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import ButtonWithText from "app/popleads/components/ui-kit/Button";
import { colorArray, tagOptions } from "../utils/constants";
import { colors } from "app/popleads/components/ui-kit/colors";
import { HexColorPicker } from "react-colorful";
import { Body1 } from "app/popleads/components/ui-kit/Text";
import ExtensionSearch from "./ExtensionSearch";
import useGorevEkle from "app/popleads/hooks/useGorevEkle";
import { toast } from "react-toastify";
import moment from "moment";
import { useExtensionContext } from "../ExtensionContext";
import { StyledCheckbox } from "app/popleads/components/ui-kit/Select";
import RichTextEditor from "app/popleads/components/ui-kit/RichTextEditor";
import useTaskUpdate from "app/popleads/hooks/useTaskUpdate";
import Spinner, { INHERIT } from "app/popleads/components/ui-kit/Spinner";
import useGetTaskLists from "app/popleads/hooks/useGetTaskLists";
import { useSelector } from "react-redux";
import removeTrailingEmptyNodes from "../utils/removeTrailingEmptyNodes";
import { apiErrorText } from "app/popleads/networking/apiErrorText";

const StyledContainer = styled("div")`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    font-family: "Poppins_PL";
    font-weight: normal;
    position: relative;
    height: 100%;
`;
const StyledNewTaskContainer = styled("div")`
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex: 1;
    overflow-y: auto;
`;

const ColorsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(12, 20px);
    align-items: center;
    grid-column-gap: 11px;
    grid-row-gap: 10px;
`;
const ColorCircle = styled.div`
    width: ${(props) => (props.selected ? "12px" : "20px")};
    height: ${(props) => (props.selected ? "12px" : "20px")};
    border-radius: 50%;
    background-color: ${(props) => props.color};
    cursor: pointer;
    position: relative;
`;
const ColorItem = styled.div`
    border: ${(props) => (props.selected ? `1px solid ${colors.l3rnTech[900]}` : " ")};
    display: flex;
    justify-content: center;
    width: 20px;
    height: 20px;
    align-items: center;
    border-radius: 50%;
`;
const AddColorButton = styled.div`
    width: 12px;
    height: 12px;
    cursor: pointer;
`;

const StyledColorPickerContainer = styled.div`
    width: max-content;
    border: 1px solid #ffff !important;
    border-radius: 8px !important;
    box-shadow: 0px 2px 8px #49698c1a !important;
    padding: 16px;
    .react-colorful__pointer {
        width: 12px;
        height: 12px;
    }
`;
const StyledHexLabel = styled(Body1)`
    color: ${colors.neutral[900]};
`;
const StyledHexValue = styled(Body1)`
    color: #1e416e;
`;
const StyledHexContainer = styled.div`
    display: flex;
    justify-content: center;
    gap: 10px;
    align-items: center;
    margin-top: 12px;
`;
const StyledColorPickerButtonContainer = styled(StyledHexContainer)`
    border-top: 1px solid ${colors.passive[100]};
    margin-top: 16px;
    padding-top: 12px;
`;
const StyledCheckboxContainer = styled("div")`
    display: flex;
    gap: 14px;
    align-items: center;
`;
const StyledCheckboxLabel = styled("label")`
    margin: 0;
    font-size: 0.91563rem;
    font-weight: 400;
    line-height: 1.2938rem;
`;

export const StyledFooterButton = styled("div")`
    // position: absolute;
    z-index: 2;
    background: #fff;
    width: inherit;
    display: flex;
    width: 410px;
    justify-content: end;
    padding: 12px;
    bottom: 0;
    border-top: 1px solid ${colors.passive[100]};
`;

const StyledForm = styled(Form)`
    height: 100%;
    display: flex;
    flex-direction: column;
`;

const validationSchema = Yup.object().shape({
    important: Yup.boolean(),
    customerHappiness: Yup.boolean(),
    assignToPerson: Yup.array().nullable(),
    assignToActivity: Yup.string().nullable(),
    taskType: Yup.string().required("Task type is required"),
    relatedCustomer: Yup.string().nullable(),
    dateTime: Yup.string().required("Date and time is required"),
    description: Yup.string().required("Description is required"),
});

const ExtensionNewTask = () => {
    const [color, setColor] = useState("#333");
    const [colorList, setColorList] = useState(colorArray);
    const [openColorPicker, setOpenColorPicker] = useState(false);
    const [showActivitySelect, setShowActivitySelect] = useState(false);
    const [resetTaskType, setResetTaskType] = useState(false);
    const [isActivityLoading, setIsActivityLoading] = useState(false);

    const [previousActivity, setPreviousActivity] = useState(null);
    const [previousPersons, setPreviousPersons] = useState([]);

    const { gorevEkle, loading } = useGorevEkle();
    const { refetch: fetchTaskLists } = useGetTaskLists();
    const pickerRef = useRef();
    const {
        profile,
        companyUsers,
        userActivities,
        fetchUserExtensionActivitiesFunc,
        taskPage,
        selectedTask,
        editMode,
        setState,
        selectedDate,
        refetchTaskList,
        fetchStaticTaskList,
    } = useExtensionContext();

    const getRoundedCurrentTime = () => {
        const now = moment();
        const minutes = now.minutes();

        if (minutes < 15) {
            now.minutes(0).seconds(0);
        } else if (minutes < 45) {
            now.minutes(30).seconds(0);
        } else {
            now.add(1, "hour")
                .minutes(0)
                .seconds(0);
        }

        return now.format("YYYY-MM-DDTHH:mm");
    };

    const initialValues = useMemo(
        () =>
            editMode
                ? {
                      important: !!selectedTask?.important,
                      customerHappiness: !!selectedTask?.customerHappiness,
                      assignToPerson:
                          selectedTask?.assignedToIds?.map((id) => ({
                              label: companyUsers.find((user) => user.id === id)?.name,
                              value: id,
                          })) || [],
                      assignToActivity: selectedTask?.activityId
                          ? {
                                label: userActivities.find((activity) => activity.id === selectedTask.activityId)
                                    ?.subject,
                                value: selectedTask.activityId,
                            }
                          : "",
                      taskType: selectedTask?.type || "",
                      relatedCustomer: selectedTask?.profilId || "",
                      dateTime: selectedTask?.zaman
                          ? moment(selectedTask.zaman).format("YYYY-MM-DDTHH:mm")
                          : getRoundedCurrentTime(),
                      description: selectedTask?.metin || "",
                  }
                : {
                      important: false,
                      customerHappiness: false,
                      assignToPerson: [],
                      assignToActivity: "",
                      taskType: "",
                      relatedCustomer: "",
                      dateTime: getRoundedCurrentTime(),
                      description: "",
                  },
        [editMode, selectedTask, companyUsers, userActivities]
    );
    const { updateTask } = useTaskUpdate();

    const editorConfig = {
        toolbar: ["bold", "italic", "bulletedList", "numberedList"],
        link: {
            addTargetToExternalLinks: true,
        },
    };

    const [selectedColor, setSelectedColor] = useState("#333");

    const { user: loggedInUser } = useSelector((state) => state.auth);
    const customerFollowUpsPermission = loggedInUser?.permissions?.CUSTOMER_HAPPINESS_TASK === 1;

    useEffect(() => {
        if (!editMode) {
            setSelectedColor(colorArray[0]);
        }
    }, [editMode]);

    useEffect(() => {
        if (editMode && selectedTask?.taskColour) {
            const taskColor = selectedTask.taskColour;
            let hexColor;

            if (taskColor.startsWith("rgb")) {
                hexColor = rgbToHex(taskColor);
            } else if (taskColor.startsWith("#")) {
                hexColor = taskColor;
            } else {
                hexColor = taskColor;
            }

            setSelectedColor(hexColor);

            const colorExists = colorList.some((color) => isColorMatch(color, hexColor));
            if (!colorExists) {
                setColorList((prevList) => [...prevList, hexColor]);
            }
        }
    }, [editMode, selectedTask, colorList]);

    const handleSaveColor = () => {
        const colorExists = colorList.some((existingColor) => isColorMatch(existingColor, color));

        if (!colorExists) {
            setColorList([...colorList, color]);
            setSelectedColor(color);
        }
        setOpenColorPicker(false);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target)) {
                setOpenColorPicker(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (showActivitySelect && userActivities.length === 0) {
            setIsActivityLoading(true);
            fetchUserExtensionActivitiesFunc().finally(() => {
                setIsActivityLoading(false);
            });
        }
    }, [showActivitySelect]);

    useEffect(() => {
        if (editMode && selectedTask?.activityId && userActivities.length === 0) {
            setIsActivityLoading(true);
            fetchUserExtensionActivitiesFunc().finally(() => {
                setIsActivityLoading(false);
            });
        }
    }, [editMode, selectedTask]);

    useEffect(() => {
        if (editMode && selectedTask?.activityId) {
            const activity = {
                label: userActivities.find((activity) => activity.id === selectedTask.activityId)?.subject,
                value: selectedTask.activityId,
            };
            setPreviousActivity(activity);
        }
    }, [editMode, selectedTask, userActivities]);

    useEffect(() => {
        if (editMode && selectedTask?.activityId) {
            setShowActivitySelect(true);
        }
    }, [editMode, selectedTask]);

    const handleSubmit = async (values, { setSubmitting, resetForm, validateForm }) => {
        try {
            const errors = await validateForm(values);
            if (Object.keys(errors).length > 0) {
                const firstError = Object.values(errors)[0];
                toast.error(firstError);
                return;
            }

            if (values.assignToActivity?.value && values.assignToPerson?.length > 0) {
                toast.error("You cannot assign a task to both an activity and a person at the same time.");
                return;
            }

            setSubmitting(true);
            const payload = {
                metin: removeTrailingEmptyNodes(values.description) || "",
                zaman: values.dateTime ? new Date(values.dateTime).getTime() : moment().valueOf(),
                profilId: taskPage ? (values.relatedCustomer ? values.relatedCustomer : "") : profile?.id || "",
                guncelleProfilId: taskPage ? (values.relatedCustomer === profile?.id ? profile.id : "") : profile.id,
                type: values.taskType,
                important: values.important ? 1 : 0,
                taskColour: hexToRgb(selectedColor),
                assignedToIds: values.assignToPerson?.length > 0 ? values.assignToPerson.map((item) => item.value) : [],
                activityId: values.assignToActivity?.value || "",
                customerHappiness: values.customerHappiness ? 1 : 0,
            };

            let response;
            if (editMode) {
                response = await updateTask({
                    taskId: selectedTask.id,
                    taskText: payload.metin,
                    currentDate: payload.zaman,
                    profilId: taskPage ? (values.relatedCustomer ? values.relatedCustomer : "") : profile?.id || "",
                    daysToAdd: 0,
                    type: payload.type,
                    important: payload.important,
                    taskColour: payload.taskColour,
                    assignedToIds: payload.assignedToIds,
                    activityId: payload.activityId,
                    customerHappiness: payload.customerHappiness,
                });
                if (response?.data && response?.status === 200) {
                    toast.success("Task updated successfully");
                    setState((prevState) => ({
                        ...prevState,
                        refreshTaskList: true,
                    }));
                    await fetchTaskLists({
                        selectedDate: selectedDate || new Date(),
                        forceRefresh: true,
                    });
                    //refetchProfile();
                    refetchTaskList();
                    await fetchStaticTaskList();
                }
            } else {
                response = await gorevEkle(payload);
                if (response?.data && response?.status === 200) {
                    toast.success("Task created successfully");
                    setState((prevState) => ({
                        ...prevState,
                        refreshTaskList: true,
                    }));
                    await fetchTaskLists({
                        selectedDate: selectedDate || new Date(),
                        //forceRefresh: true,
                    });
                    //refetchProfile();
                    refetchTaskList();
                    await fetchStaticTaskList();
                }
            }

            if (response?.data && response?.status === 200) {
                const stripHtmlTags = (html) => {
                    const doc = new DOMParser().parseFromString(html, "text/html");
                    return doc.body.textContent || "";
                };

                const messagePayload = {
                    message: "XPIDER_MESSAGE_NOTIFICATION",
                    task: {
                        id: response.data.id,
                        profilId: payload.profilId,
                        metin: stripHtmlTags(payload.metin),
                        zaman: payload.zaman,
                        important: payload.important,
                        customerHappiness: payload.customerHappiness,
                        taskColour: selectedColor,
                        assignedToIds: payload.assignedToIds,
                        activityId: payload.activityId || null,
                    },
                };
                window.parent.postMessage(messagePayload, "*");

                if (taskPage) {
                    setState((prevState) => ({
                        ...prevState,
                        popupContent: null,
                        popupIsOpen: false,
                    }));
                } else {
                    setState((prevState) => ({
                        ...prevState,
                    }));
                }
                resetForm();
                setSelectedColor("");
                setResetTaskType((prev) => !prev);
            }
        } catch (error) {
            toast.error(apiErrorText(error));
        } finally {
            setSubmitting(false);
            setState((prevState) => ({
                ...prevState,
                editMode: false,
            }));
        }
    };

    function hexToRgb(hex) {
        if (!hex || typeof hex !== "string" || hex.length < 6) {
            return "rgb(0, 0, 0)";
        }
        hex = hex.replace("#", "");
        let r = parseInt(hex.substring(0, 2), 16);
        let g = parseInt(hex.substring(2, 4), 16);
        let b = parseInt(hex.substring(4, 6), 16);
        return `rgb(${r}, ${g}, ${b})`;
    }

    function rgbToHex(rgb) {
        if (!rgb || typeof rgb !== "string") {
            return "#333333";
        }
        const match = rgb.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
        if (!match) return "#333333";

        const r = parseInt(match[1]);
        const g = parseInt(match[2]);
        const b = parseInt(match[3]);

        return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
    }

    function isColorMatch(color1, color2) {
        if (!color1 || !color2) return false;

        let hex1 = color1;
        let hex2 = color2;

        if (color1.startsWith("rgb")) {
            hex1 = rgbToHex(color1);
        }
        if (color2.startsWith("rgb")) {
            hex2 = rgbToHex(color2);
        }

        hex1 = hex1.replace("#", "").toLowerCase();
        hex2 = hex2.replace("#", "").toLowerCase();

        return hex1 === hex2;
    }

    const newCompanyUsers = companyUsers.map((user) => {
        return {
            name: user.id === loggedInUser.id ? "ME" : user.name,
            id: user.id,
        };
    });

    return (
        <StyledContainer>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize={true}
            >
                {({
                    setFieldValue,
                    values,
                    errors,
                    touched,
                    handleSubmit,
                    validateForm,
                    setTouched,
                    setErrors,
                    isSubmitting,
                }) => {
                    const onSubmit = async (e) => {
                        e.preventDefault();

                        const touchedFields = Object.keys(initialValues).reduce((acc, key) => {
                            acc[key] = true;
                            return acc;
                        }, {});
                        await setTouched(touchedFields);

                        const validationErrors = await validateForm();
                        if (Object.keys(validationErrors).length > 0) {
                            setErrors(validationErrors);
                            const firstError = Object.values(validationErrors)[0];
                            toast.error(firstError);
                            return;
                        }

                        handleSubmit(e);
                    };

                    return (
                        <StyledForm onSubmit={onSubmit}>
                            <StyledNewTaskContainer>
                                <StyledCheckboxContainer>
                                    <StyledCheckbox
                                        variant="l3rnTech"
                                        id="important"
                                        onChange={(e) => {
                                            e.persist();
                                            setFieldValue("important", e.target.checked);
                                        }}
                                        checked={values.important}
                                    />
                                    <StyledCheckboxLabel htmlFor="important">Important</StyledCheckboxLabel>
                                </StyledCheckboxContainer>

                                {customerFollowUpsPermission && !editMode && (
                                    <StyledCheckboxContainer>
                                        <StyledCheckbox
                                            variant="l3rnTech"
                                            id="customerHappiness"
                                            onChange={(e) => {
                                                e.persist();
                                                setFieldValue("customerHappiness", e.target.checked);
                                            }}
                                            checked={values.customerHappiness}
                                        />
                                        <StyledCheckboxLabel htmlFor="customerHappiness">
                                            Customer Follow Ups
                                        </StyledCheckboxLabel>
                                    </StyledCheckboxContainer>
                                )}

                                <ExtensionInputGroup
                                    variant="l3rnTech"
                                    label="Task Type"
                                    type="select"
                                    placeholder="Please Select A Task Type"
                                    options={tagOptions}
                                    onChange={(selected) => {
                                        setFieldValue("taskType", selected?.value);
                                    }}
                                    value={
                                        values.taskType
                                            ? tagOptions.find((option) => option.value === values.taskType)
                                            : null
                                    }
                                    key={`task-type-${resetTaskType}`}
                                />
                                {taskPage && (
                                    <div>
                                        <StyledLabel>Related Customer (optional)</StyledLabel>
                                        <ExtensionSearch
                                            optionClick={(selected) => setFieldValue("relatedCustomer", selected)}
                                            disabled={editMode}
                                        />
                                    </div>
                                )}

                                <ExtensionInputGroup
                                    label="Set Date & Time"
                                    type="date"
                                    timeStamp={true}
                                    datePlaceholder="dd/mm/yyyy hh/mm"
                                    onChange={(date) => {
                                        if (date) {
                                            const selectedDate = new Date(date);
                                            const formattedDate = selectedDate.toISOString().slice(0, 16);
                                            setFieldValue("dateTime", formattedDate);
                                        } else {
                                            setFieldValue("dateTime", "");
                                        }
                                    }}
                                    value={values.dateTime}
                                    noTimeSelection={false}
                                    showMonthYearDropdown={true}
                                />
                                <div>
                                    <StyledLabel>Description</StyledLabel>
                                    <RichTextEditor
                                        variant="l3rnTech"
                                        minHeight={"150px"}
                                        maxHeight={"400px"}
                                        config={editorConfig}
                                        data={values.description}
                                        resizable={true}
                                        onChange={(event, editor) => {
                                            const editorData = editor.getData();
                                            setFieldValue("description", editorData);
                                        }}
                                    />
                                </div>
                                {taskPage && showActivitySelect && !editMode ? (
                                    <ExtensionInputGroup
                                        variant="l3rnTech"
                                        key={"input_1"}
                                        label="Choose an Activity (optional)"
                                        type="select"
                                        defaultOptions={userActivities
                                            .map((item) => {
                                                return {
                                                    label: item.subject,
                                                    value: item.id,
                                                };
                                            })
                                            .slice(0, 10)}
                                        loadOptions={(input, callback) => {
                                            callback(
                                                userActivities
                                                    .filter((activity) =>
                                                        activity.subject.toLowerCase().includes(input.toLowerCase())
                                                    )
                                                    .map((item) => {
                                                        return {
                                                            label: item.subject,
                                                            value: item.id,
                                                        };
                                                    })
                                                    .slice(0, 10)
                                            );
                                        }}
                                        onChange={(selected) => setFieldValue("assignToActivity", selected)}
                                        value={values.assignToActivity}
                                        isLoading={isActivityLoading}
                                    />
                                ) : (
                                    taskPage &&
                                    !editMode && (
                                        <div style={{ position: "relative" }}>
                                            <ExtensionInputGroup
                                                variant="l3rnTech"
                                                key={"input_22"}
                                                label="Assign to Another Person (optional)"
                                                type="select"
                                                defaultOptions
                                                isMulti
                                                additional={{ page: 1 }}
                                                loadOptions={(input, callback) =>
                                                    callback(
                                                        newCompanyUsers
                                                            .filter((user) =>
                                                                user.name.toLowerCase().includes(input.toLowerCase())
                                                            )
                                                            .map((item) => {
                                                                return {
                                                                    label: item.name,
                                                                    value: item.id,
                                                                };
                                                            })
                                                    )
                                                }
                                                onChange={(selected) => setFieldValue("assignToPerson", selected)}
                                                value={values.assignToPerson}
                                            />
                                        </div>
                                    )
                                )}
                                {taskPage && !editMode && (
                                    <StyledCheckboxContainer>
                                        <StyledCheckbox
                                            variant="l3rnTech"
                                            id="activity-check"
                                            onChange={(e) => {
                                                e.persist();
                                                const newState = !showActivitySelect;
                                                setShowActivitySelect(newState);

                                                if (newState) {
                                                    setPreviousPersons(values.assignToPerson || []);
                                                    setFieldValue("assignToPerson", []);

                                                    if (previousActivity) {
                                                        setFieldValue("assignToActivity", previousActivity);
                                                    }
                                                } else {
                                                    setPreviousActivity(values.assignToActivity || "");
                                                    setFieldValue("assignToActivity", "");

                                                    if (previousPersons && previousPersons.length > 0) {
                                                        setFieldValue("assignToPerson", previousPersons);
                                                    }
                                                }
                                            }}
                                            checked={showActivitySelect}
                                        />
                                        <StyledCheckboxLabel htmlFor="activity-check">
                                            {"Assign to an Activity"}
                                        </StyledCheckboxLabel>
                                    </StyledCheckboxContainer>
                                )}

                                <div>
                                    <StyledLabel>Pick Color</StyledLabel>
                                    <ColorsGrid>
                                        {colorList.map((color, index) => (
                                            <ColorItem key={index} selected={isColorMatch(selectedColor, color)}>
                                                <ColorCircle
                                                    selected={isColorMatch(selectedColor, color)}
                                                    color={color}
                                                    onClick={() => setSelectedColor(color)}
                                                />
                                            </ColorItem>
                                        ))}
                                        <AddColorButton
                                            onClick={() => {
                                                const randomColor =
                                                    "#" +
                                                    Math.floor(Math.random() * 16777215)
                                                        .toString(16)
                                                        .padStart(6, "0");
                                                setColor(randomColor);
                                                setOpenColorPicker(true);
                                            }}
                                        >
                                            +
                                        </AddColorButton>
                                    </ColorsGrid>
                                </div>
                            </StyledNewTaskContainer>
                            {openColorPicker && (
                                <StyledColorPickerContainer ref={pickerRef}>
                                    <HexColorPicker color={color} onChange={setColor} />
                                    <StyledHexContainer>
                                        <StyledHexLabel>Hex :</StyledHexLabel>
                                        <StyledHexValue>{color}</StyledHexValue>
                                    </StyledHexContainer>
                                    <StyledColorPickerButtonContainer>
                                        <ButtonWithText
                                            onClick={() => setOpenColorPicker(false)}
                                            size="small"
                                            variant="passive"
                                            buttonStyle="borderless"
                                        >
                                            Cancel
                                        </ButtonWithText>
                                        <ButtonWithText
                                            size="small"
                                            variant="success"
                                            type="default"
                                            disabled={!color || color.length < 4}
                                            buttonStyle="default"
                                            onClick={handleSaveColor}
                                        >
                                            Add New Color
                                        </ButtonWithText>
                                    </StyledColorPickerButtonContainer>
                                </StyledColorPickerContainer>
                            )}
                            <StyledFooterButton>
                                <ButtonWithText
                                    size="xsmall"
                                    variant="l3rnTech"
                                    buttonStyle="default"
                                    type="submit"
                                    disabled={isSubmitting || loading}
                                >
                                    {isSubmitting || loading ? (
                                        <>
                                            <Spinner
                                                decorative
                                                variant={INHERIT}
                                                size={16}
                                                thickness={3}
                                                style={{ marginRight: "5px" }}
                                            />
                                            {editMode ? "Updating..." : "Adding..."}
                                        </>
                                    ) : editMode ? (
                                        "Edit Task"
                                    ) : (
                                        "Add Task"
                                    )}
                                </ButtonWithText>
                            </StyledFooterButton>
                        </StyledForm>
                    );
                }}
            </Formik>
        </StyledContainer>
    );
};

export default ExtensionNewTask;
